/**
 * Agency › Cards — the operator approves each client-facing dashboard card ONE at
 * a time (founder 2026-07-12). Every card shows, in very plain language, what it
 * means, the arithmetic, and a worked example — plus how it behaves with the date
 * picker. Approve lets a card onto real client accounts; Reject hides it there
 * (demo/test always show everything). Grandfathered cards stay visible while
 * pending, so approving is at the operator's pace and nothing breaks at ship.
 *
 * Mirrors the other agency pages: operator gate → useQuery → tabs + search →
 * grouped, expandable rows with status chips and row actions.
 */

import { useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "../lib/api-context.js";
import { useToast } from "../lib/toast.js";
import { Card } from "../components/cards.js";
import { Segmented } from "../components/Segmented.js";
import { isLaterShelf } from "@data-dashboard/shared";

type DerivedStatus = "approved" | "pending" | "rejected" | "changed" | "question";
/** The three client-visible things the operator can edit per card. */
type OverrideField = "name" | "what" | "note";

interface CardPlacement {
  pageKey: string;
  page: string;
  renderId: string;
}

interface CardRow {
  id: string;
  page: string;
  pageKey: string;
  /** "Exec dashboard + Marketing" — every page this one card appears on. */
  pagesLabel: string;
  placements: CardPlacement[];
  title: string;
  /** How the live card renders its title suffix — "{timeline}" (window picker
   *  label), a fixed literal, or undefined (plain). Display-only. */
  titleSuffix?: string;
  /** Operator rename override (what clients see), or null → the catalog title. */
  displayName: string | null;
  /** Client ⓘ overrides (what-it-is / note), or null → coded default. */
  displayWhat: string | null;
  displayNote: string | null;
  /** Company-wide badge: coded default, operator override (or null), can-show. */
  companyWideDefault: boolean;
  companyWideOverride: boolean | null;
  companyWideCapable: boolean;
  /** Demo deep-link (path incl. ?spotlight=), or null if not previewable. */
  previewPath: string | null;
  previewPage: string | null;
  definition: string;
  arithmetic: string;
  example: string;
  windowBehavior: string;
  metricIds: string[];
  note?: string;
  /** Operator-only meta shown near the status chip — never a client-copy box. */
  opsNote?: string;
  grandfathered: boolean;
  status: DerivedStatus;
  /** True when this card is in review because we reworded its note/definition (no
   *  math change) — shows the "note wording changed" tag. Cleared on re-approve. */
  noteChanged: boolean;
  noteChangedAt: string | null;
  definitionHash: string;
  approvedHash: string | null;
  approvedBy: string | null;
  approvedAt: string | null;
  notes: string | null;
  /** Card-review DRAFT note ("what's wrong / what should change"), independent of approve/reject. */
  draftNote: string | null;
  draftNoteAt: string | null;
  /** When feedback was last batch-submitted for this card. */
  feedbackSubmittedAt: string | null;
}

interface CardsPayload {
  cards: CardRow[];
  counts: { total: number; approved: number; pending: number; rejected: number; changed: number; noted: number; question: number };
  pendingCount: number;
}

type Tab = "all" | "pending" | "approved" | "rejected" | "noted" | "tbd" | "later";

/** The title the way the live card renders it: base + " · <suffix>". The suffix is
 *  the literal "{timeline}" placeholder (the window picker's label) or a fixed
 *  qualifier ("right now", "all-time"…). The editable override edits ONLY the base. */
function renderedTitle(baseTitle: string, suffix?: string): string {
  return suffix ? `${baseTitle} · ${suffix}` : baseTitle;
}

export default function AgencyCardsPage(): React.ReactElement {
  const api = useApiClient();
  const toast = useToast();
  const qc = useQueryClient();

  const status = useQuery({
    queryKey: ["account", "agency-status"],
    queryFn: () => api.get<{ isOperator: boolean }>("/account/agency-status"),
  });
  const isOperator = status.data?.isOperator ?? false;

  const q = useQuery({
    queryKey: ["admin", "cards"],
    enabled: isOperator,
    queryFn: () => api.get<CardsPayload>("/admin/cards"),
  });

  const [tab, setTab] = useState<Tab>("pending");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState<string | null>(null);
  // Approving/rejecting a card correctly removes it from Needs review. To stop the
  // page from jumping when that row disappears (founder 2026-07-14: "the whole page
  // renders and I lose the card"), anchor to the NEXT card's on-screen position
  // rather than holding a raw scroll pixel — a raw pixel breaks the instant content
  // ABOVE your eye shrinks (exactly the common case: you're looking at the row you
  // just acted on), since "same pixel" then shows different content entirely. This
  // measures the next surviving card's position before the row leaves, then nudges
  // scroll by the exact delta after the reflow so that card lands right back where
  // your eye already was. (Two earlier attempts: FREEZING the row in its old tab —
  // wrongly kept approved cards visible in Needs review too; holding raw scrollY —
  // wrong the moment anything above the viewport changed height.)
  const anchorScroll = (actedId: string): void => {
    const idx = visible.findIndex((c) => c.id === actedId);
    const nextId = idx >= 0 ? visible[idx + 1]?.id : undefined;
    if (!nextId) return;
    const before = document.querySelector(`[data-card-row="${CSS.escape(nextId)}"]`);
    if (!before) return;
    const beforeTop = before.getBoundingClientRect().top;
    requestAnimationFrame(() => {
      const after = document.querySelector(`[data-card-row="${CSS.escape(nextId)}"]`);
      if (!after) return;
      const afterTop = after.getBoundingClientRect().top;
      if (afterTop !== beforeTop) window.scrollBy({ top: afterTop - beforeTop });
    });
  };
  // Which noted cards are EXcluded from the next submit. Tracking the deselected
  // set (not the selected set) means a freshly-noted card is included by default
  // without any effect to keep selection in sync.
  const [deselected, setDeselected] = useState<Set<string>>(new Set());

  // NOTE TEXT LIVES AT PAGE LEVEL (founder lost notes 2026-07-13): the old
  // textarea was uncontrolled inside the expanded row, so collapsing the row —
  // or expanding another card — unmounted it WITHOUT firing blur, and the text
  // vanished before it ever saved. Now the text survives collapse, saves on a
  // short debounce WHILE typing (blur is a backstop, not the mechanism), and a
  // visible Saving…/Saved ✓ chip makes a silent failure impossible.
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [saveState, setSaveState] = useState<Record<string, "saving" | "saved" | "error">>({});
  const saveTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  /** The text the box shows: the local draft wins; server value is the base. */
  const noteValue = (c: CardRow): string => drafts[c.id] ?? c.draftNote ?? "";

  const setSaved = (id: string, s: "saving" | "saved" | "error"): void =>
    setSaveState((prev) => ({ ...prev, [id]: s }));

  // DISPLAY OVERRIDES — what CLIENTS see (the card's title + its ⓘ "What it is"
  // and "Note"). Each saves on a debounce with its own Saved ✓ chip, exactly like
  // the review note, and applies to every account (incl. the demo) on the next
  // load. Empty box = the coded default. Keyed by `${cardId}:${field}`.
  const [ov, setOv] = useState<Record<string, string>>({});
  const [ovState, setOvState] = useState<Record<string, "saving" | "saved" | "error">>({});
  const ovTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const ovKey = (id: string, f: OverrideField): string => `${id}:${f}`;
  const setOvSaved = (k: string, s: "saving" | "saved" | "error"): void =>
    setOvState((prev) => ({ ...prev, [k]: s }));

  /** The override box value: local draft wins; else the saved override; else empty
   *  (empty = use the coded default, shown as the placeholder). */
  /** The RECOMMENDED client-facing text (founder 2026-07-13: prefill the real
   *  text so he tweaks instead of authoring ~100 cards from scratch). */
  const ovDefault = (c: CardRow, f: OverrideField): string => {
    if (f === "name") return c.title;
    if (f === "what") return c.definition;
    return c.note ?? "";
  };

  const ovValue = (c: CardRow, f: OverrideField): string => {
    const k = ovKey(c.id, f);
    if (ov[k] !== undefined) return ov[k];
    if (f === "name") return c.displayName ?? ovDefault(c, "name");
    if (f === "what") return c.displayWhat ?? ovDefault(c, "what");
    return c.displayNote ?? ovDefault(c, "note");
  };

  const saveOverride = useMutation({
    mutationFn: (v: { id: string; field: OverrideField; value: string }) => {
      if (v.field === "name") return api.put(`/admin/cards/${v.id}/name`, { name: v.value });
      return api.put(`/admin/cards/${v.id}/explain`, v.field === "what" ? { what: v.value } : { note: v.value });
    },
    onMutate: (v) => setOvSaved(ovKey(v.id, v.field), "saving"),
    onSuccess: (_d, v) => {
      setOvSaved(ovKey(v.id, v.field), "saved");
      // NOTE: the local draft is deliberately KEPT (not cleared) — clearing it here
      // races a user who is still typing and eats their keystrokes. The box simply
      // keeps showing exactly what he typed; server truth re-syncs on next load.
      void qc.invalidateQueries({ queryKey: ["admin", "cards"] });
    },
    onError: (_e, v) => {
      setOvSaved(ovKey(v.id, v.field), "error");
      toast.error("Couldn't save that — it's still in the box; click Retry.");
    },
  });

  // (The old "Blended" badge toggle + its mutation were removed 2026-07-14 — it
  // never affected the math; blended-ness is a data fact and the signal now lives
  // in the card title. The server's /company-wide endpoint stays for back-compat.)

  // Saving text identical to the recommended default stores "" (no override) —
  // the normalization lives HERE at save time, never in what the box displays.
  const onOvChange = (id: string, f: OverrideField, text: string, def: string): void => {
    const k = ovKey(id, f);
    setOv((prev) => ({ ...prev, [k]: text }));
    setOvSaved(k, "saving");
    if (ovTimers.current[k]) clearTimeout(ovTimers.current[k]);
    ovTimers.current[k] = setTimeout(
      () => saveOverride.mutate({ id, field: f, value: text.trim() === def.trim() ? "" : text }),
      700,
    );
  };
  const flushOv = (id: string, f: OverrideField, def: string): void => {
    const k = ovKey(id, f);
    if (ovTimers.current[k]) {
      clearTimeout(ovTimers.current[k]);
      delete ovTimers.current[k];
      const v = ov[k];
      if (v !== undefined) saveOverride.mutate({ id, field: f, value: v.trim() === def.trim() ? "" : v });
    }
  };

  /** One override editor (title / info-what / info-note). Returns JSX (not a
   *  component) so the textarea keeps focus across the debounced re-renders. */
  const ovField = (c: CardRow, f: OverrideField, label: string, placeholder: string, hint: string, multiline: boolean): React.ReactElement => {
    const k = ovKey(c.id, f);
    const st = ovState[k];
    const common = {
      // The recommended text sits IN the box as real, editable content (founder:
      // "put the actual text in there... I will just edit it" — a grey placeholder
      // forces him to retype from scratch). Untouched-default still saves as ""
      // (no override) — that normalization happens at save time, not here.
      value: ovValue(c, f),
      onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => onOvChange(c.id, f, e.target.value, ovDefault(c, f)),
      onBlur: () => flushOv(c.id, f, ovDefault(c, f)),
      placeholder,
    };
    return (
      <div style={{ marginTop: 11 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={styles.noteLabel}>{label}</div>
          {st === "saving" && <span style={saveChip("var(--body-faint)")}>Saving…</span>}
          {st === "saved" && <span style={saveChip("var(--good)")}>Saved ✓</span>}
          {st === "error" && (
            <button type="button" style={{ ...saveChip("var(--bad)"), cursor: "pointer", border: "none" }}
              onClick={() => saveOverride.mutate({ id: c.id, field: f, value: ovValue(c, f).trim() === ovDefault(c, f).trim() ? "" : ovValue(c, f) })}>
              Couldn&rsquo;t save — Retry
            </button>
          )}
        </div>
        {multiline
          ? <textarea {...common} style={{ ...styles.noteBox, minHeight: 54 }} />
          : <input {...common} style={styles.ovInput} />}
        <div style={{ fontSize: 11.5, color: "var(--body-faint)", marginTop: 4 }}>{hint}</div>
      </div>
    );
  };

  const decide = useMutation({
    mutationFn: (v: { id: string; action: "approve" | "reject"; notes?: string }) =>
      api.post(`/admin/cards/${v.id}/${v.action}`, v.notes ? { notes: v.notes } : undefined),
    // Optimistic: flip the card's chip immediately so the click feels instant.
    // Anchor scroll to the next card so the row leaving Needs review doesn't bounce you.
    onMutate: async (v) => {
      anchorScroll(v.id);
      await qc.cancelQueries({ queryKey: ["admin", "cards"] });
      const prev = qc.getQueryData<CardsPayload>(["admin", "cards"]);
      if (prev) {
        qc.setQueryData<CardsPayload>(["admin", "cards"], {
          ...prev,
          cards: prev.cards.map((c) =>
            // Approving/rejecting also clears the "note wording changed" tag (server does
            // too) so the amber chip disappears the instant he acts.
            c.id === v.id ? { ...c, status: v.action === "approve" ? "approved" : "rejected", noteChanged: false } : c,
          ),
        });
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(["admin", "cards"], ctx.prev);
      toast.error("Couldn't save that — try again.");
    },
    onSuccess: (_d, v) => toast.success(v.action === "approve" ? "Approved." : "Rejected — hidden from client accounts."),
    // Re-sync counts + the nav badge from the server.
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "cards"] });
    },
  });

  // Card-review DRAFT note — saves on a debounce while typing (+ blur backstop
  // + a forced flush before any Approve/Reject/Ask). Independent of
  // approve/reject: saving a note never changes a card's status. The local
  // draft is NEVER rolled back on error — the founder's text stays on screen
  // with a retry chip.
  const saveNote = useMutation({
    mutationFn: (v: { id: string; note: string }) => api.put(`/admin/cards/${v.id}/note`, { note: v.note }),
    onMutate: async (v) => {
      setSaved(v.id, "saving");
      await qc.cancelQueries({ queryKey: ["admin", "cards"] });
      const prev = qc.getQueryData<CardsPayload>(["admin", "cards"]);
      if (prev) qc.setQueryData<CardsPayload>(["admin", "cards"], applyNoteToCache(prev, v.id, v.note));
      return { prev };
    },
    onSuccess: (_d, v) => setSaved(v.id, "saved"),
    onError: (_e, v) => {
      setSaved(v.id, "error");
      toast.error("Couldn't save your note — it's still in the box; click Retry.");
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "cards"] });
    },
  });

  /** Debounced save while typing (700ms after the last keystroke). */
  const onNoteChange = (id: string, text: string): void => {
    setDrafts((prev) => ({ ...prev, [id]: text }));
    setSaved(id, "saving");
    const timers = saveTimers.current;
    if (timers[id]) clearTimeout(timers[id]);
    timers[id] = setTimeout(() => {
      saveNote.mutate({ id, note: text });
    }, 700);
  };

  /** Flush any pending debounce NOW (blur, or right before an action). */
  const flushNote = (c: CardRow): void => {
    const timers = saveTimers.current;
    if (timers[c.id]) {
      clearTimeout(timers[c.id]);
      delete timers[c.id];
    }
    const val = drafts[c.id];
    if (val !== undefined && val.trim() !== (c.draftNote ?? "").trim()) {
      saveNote.mutate({ id: c.id, note: val });
    }
  };

  // "Ask a question" (founder 2026-07-13) — parks the card in the TBD tab with
  // the note text as the question. Clients still see the card.
  const ask = useMutation({
    mutationFn: (v: { id: string; question: string }) => api.post(`/admin/cards/${v.id}/question`, { question: v.question }),
    onMutate: async (v) => {
      anchorScroll(v.id);
      await qc.cancelQueries({ queryKey: ["admin", "cards"] });
      const prev = qc.getQueryData<CardsPayload>(["admin", "cards"]);
      if (prev) {
        qc.setQueryData<CardsPayload>(["admin", "cards"], {
          ...prev,
          cards: prev.cards.map((c) => (c.id === v.id ? { ...c, status: "question" as const, draftNote: v.question } : c)),
        });
      }
      return { prev };
    },
    onSuccess: () => toast.success("Question saved — card parked in TBD. Submit feedback to send it over."),
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(["admin", "cards"], ctx.prev);
      toast.error("Couldn't save the question — try again.");
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "cards"] });
    },
  });

  // Batch-submit the drafted notes (the selected subset) into Feedback & Fixes —
  // one report per card. After it lands the drafts clear and the queue refreshes.
  const submitFeedback = useMutation({
    mutationFn: (cardIds: string[]) => api.post<{ ok: boolean; submitted: number }>("/admin/cards/feedback", { cardIds }),
    onSuccess: (d, cardIds) => {
      setDeselected(new Set());
      // Kill any PENDING autosave timers for the submitted cards FIRST — a
      // debounce firing after this success would re-save the stale text the
      // server just cleared (the "8 left to submit" resurrection).
      for (const id of cardIds) {
        const t = saveTimers.current[id];
        if (t) {
          clearTimeout(t);
          delete saveTimers.current[id];
        }
      }
      // Clear the LOCAL draft copies of what was just submitted — the server
      // cleared its side, but noteValue() prefers local text, so stale local
      // drafts kept the boxes full and the submit count nonzero (founder saw
      // "11 to submit" after submitting all 13).
      setDrafts((prev) => {
        const next = { ...prev };
        for (const id of cardIds) delete next[id];
        return next;
      });
      setSaveState((prev) => {
        const next = { ...prev };
        for (const id of cardIds) delete next[id];
        return next;
      });
      void qc.invalidateQueries({ queryKey: ["admin", "cards"] });
      void qc.invalidateQueries({ queryKey: ["admin", "feedback"] });
      toast.success(`Sent ${d.submitted} card ${d.submitted === 1 ? "note" : "notes"} to Feedback & Fixes.`);
    },
    onError: () => toast.error("Couldn't submit that — try again."),
  });

  const cards = q.data?.cards ?? [];

  const isNoted = (c: CardRow): boolean => Boolean((drafts[c.id] ?? c.draftNote ?? "").trim());
  const notedCards = useMemo(() => cards.filter(isNoted), [cards, drafts]);
  const selectedIds = useMemo(
    () => notedCards.filter((c) => !deselected.has(c.id)).map((c) => c.id),
    [notedCards, deselected],
  );
  const toggleSelect = (id: string): void =>
    setDeselected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const matchesSearch = (c: CardRow, needle: string): boolean =>
    !needle ||
    c.title.toLowerCase().includes(needle) ||
    c.page.toLowerCase().includes(needle) ||
    c.definition.toLowerCase().includes(needle) ||
    c.id.toLowerCase().includes(needle);

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return cards.filter((c) => {
      // "Changed since approval" needs re-review, so it lives under Pending.
      // "Later" cards are parked out of every other tab (founder 2026-07-14).
      const later = isLaterShelf(c.id);
      const inTab = tab === "later" ? later : !later && (
        tab === "all" ||
        (tab === "pending" && (c.status === "pending" || c.status === "changed")) ||
        (tab === "approved" && c.status === "approved") ||
        (tab === "rejected" && c.status === "rejected") ||
        (tab === "tbd" && c.status === "question") ||
        (tab === "noted" && isNoted(c)));
      if (!inTab) return false;
      return matchesSearch(c, needle);
    });
  }, [cards, tab, search]);

  // Group by page, preserving catalog order — EXCEPT cards that appear on more
  // than one page, which collect into a single "Used in multiple places" group at
  // the top (founder 2026-07-14: "put them into their own duplicate section instead
  // of listing them out in 2 diff sections"). Each multi-page card is still ONE row
  // / one approval; this just stops the same card feeling scattered across sections.
  const MULTI = "Used in multiple places";
  const groups = useMemo(() => {
    const map = new Map<string, CardRow[]>();
    const push = (key: string, c: CardRow): void => {
      const arr = map.get(key) ?? [];
      arr.push(c);
      map.set(key, arr);
    };
    // The "multiple places" group must sort first, so seed it before the pages.
    map.set(MULTI, []);
    for (const c of visible) push((c.placements?.length ?? 1) > 1 ? MULTI : c.page, c);
    return [...map.entries()].filter(([, rows]) => rows.length > 0);
  }, [visible]);

  if (status.isLoading) return <p style={{ color: "var(--body)" }}>Loading…</p>;
  if (!isOperator) {
    return (
      <div>
        <h1 style={{ margin: 0, fontSize: 24 }}>Cards</h1>
        <p style={{ color: "var(--body)", marginTop: 10 }}>This area is only available to agency operators.</p>
      </div>
    );
  }

  // Tab counts are computed client-side so they EXCLUDE the parked "Later" cards
  // (founder 2026-07-14). The server counts every catalog card, which made a tab
  // like Rejected read "(1)" while its list was empty — the one rejected card was
  // parked on the Later shelf, counted but never shown here.
  const nonLater = useMemo(() => cards.filter((c) => !isLaterShelf(c.id)), [cards]);
  const counts = useMemo(() => ({
    total: nonLater.length,
    approved: nonLater.filter((c) => c.status === "approved").length,
    rejected: nonLater.filter((c) => c.status === "rejected").length,
    question: nonLater.filter((c) => c.status === "question").length,
    noted: nonLater.filter((c) => isNoted(c)).length,
  }), [nonLater, drafts]);
  const needsReview = useMemo(() => nonLater.filter((c) => c.status === "pending" || c.status === "changed").length, [nonLater]);
  const laterCount = useMemo(() => cards.filter((c) => isLaterShelf(c.id)).length, [cards]);

  return (
    <div>
      <div style={styles.kicker}>Agency</div>
      <h1 style={{ margin: "7px 0 8px", fontSize: 27 }}>Cards</h1>
      <p style={{ color: "var(--body)", fontSize: 14.5, maxWidth: 820, lineHeight: 1.5 }}>
        Approve each client-facing card once — read what it means, the math, and a worked example — and it's allowed on
        client accounts. Rejecting hides a card from real clients (your demo and test accounts always show everything).
        The cards that already existed stay visible while you review them; only a reject hides one.
      </p>
      <p style={{ color: "var(--body-faint)", fontSize: 12.5, marginTop: 6 }}>
        Previews open on the demo account&rsquo;s data. Renaming a card or editing its info box updates every client
        account (and the demo) on their next load — a rename or info edit never re-opens approval.
      </p>

      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginTop: 16 }}>
        <Segmented<Tab>
          value={tab}
          onChange={setTab}
          options={[
            { value: "all", label: `All${counts ? ` (${counts.total})` : ""}` },
            { value: "pending", label: `Needs review${needsReview ? ` (${needsReview})` : ""}` },
            { value: "approved", label: `Approved${counts ? ` (${counts.approved})` : ""}` },
            { value: "rejected", label: `Rejected${counts ? ` (${counts.rejected})` : ""}` },
            { value: "tbd", label: `TBD${counts?.question ? ` (${counts.question})` : ""}` },
            { value: "noted", label: `Noted${counts?.noted ? ` (${counts.noted})` : ""}` },
            { value: "later", label: `Later${laterCount ? ` (${laterCount})` : ""}` },
          ]}
        />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search cards…"
          style={styles.search}
        />
      </div>

      {/* Batch-submit bar — appears whenever any card carries a draft note. The
          notes stay as drafts until you send them here; sending files one report
          per selected card into Feedback & Fixes. */}
      {notedCards.length > 0 && (
        <div style={styles.submitBar}>
          <div style={{ fontSize: 13, color: "var(--body)" }}>
            <b className="dd-num" style={{ color: "var(--text)" }}>{notedCards.length}</b> card{notedCards.length === 1 ? "" : "s"} noted
            {" · "}
            <b className="dd-num" style={{ color: "var(--text)" }}>{selectedIds.length}</b> selected to submit
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            {selectedIds.length < notedCards.length ? (
              <button type="button" style={styles.linkBtn} onClick={() => setDeselected(new Set())}>
                Select all
              </button>
            ) : (
              <button type="button" style={styles.linkBtn} onClick={() => setDeselected(new Set(notedCards.map((c) => c.id)))}>
                Clear
              </button>
            )}
            <button
              type="button"
              style={selectedIds.length === 0 || submitFeedback.isPending ? styles.submitBtnDisabled : styles.submitBtn}
              disabled={selectedIds.length === 0 || submitFeedback.isPending}
              onClick={() => {
                if (selectedIds.length === 0) return;
                if (!window.confirm(`Send ${selectedIds.length} card ${selectedIds.length === 1 ? "note" : "notes"} to Feedback & Fixes?`)) return;
                submitFeedback.mutate(selectedIds);
              }}
            >
              {submitFeedback.isPending ? "Submitting…" : `Submit feedback (${selectedIds.length})`}
            </button>
          </div>
        </div>
      )}

      {q.isLoading && <p style={{ marginTop: 18, color: "var(--body)" }}>Loading the card catalog…</p>}
      {q.isError && <p style={{ marginTop: 18, color: "var(--bad)" }}>Couldn't load the cards. Refresh to retry.</p>}
      {q.data && visible.length === 0 && (
        <Card style={{ marginTop: 16 }}>
          <p style={{ color: "var(--body)", fontSize: 13.5, margin: 0 }}>No cards match this filter.</p>
        </Card>
      )}

      {groups.map(([page, rows]) => (
        <div key={page} style={{ marginTop: 22 }}>
          <div style={styles.groupHead}>
            {page} <span style={{ color: "var(--body-faint)", fontWeight: 500 }}>· {rows.length}</span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10 }}>
            {rows.map((c) => {
              const isOpen = expanded === c.id;
              const noted = isNoted(c);
              return (
                <div key={c.id} data-card-row={c.id}>
                <Card style={{ padding: 0 }}>
                  <div style={{ display: "flex", alignItems: "stretch" }}>
                    {/* Fixed-width slot so titles line up whether or not a card is noted. */}
                    <div style={styles.checkSlot}>
                      {noted && (
                        <input
                          type="checkbox"
                          checked={!deselected.has(c.id)}
                          onChange={() => toggleSelect(c.id)}
                          aria-label={`Include "${c.title}" in the next feedback submit`}
                          style={{ cursor: "pointer", width: 15, height: 15, accentColor: "var(--accent)" }}
                        />
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => setExpanded(isOpen ? null : c.id)}
                      style={styles.rowBtn}
                      aria-expanded={isOpen}
                    >
                      <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                        <div style={{ fontWeight: 600, fontSize: 14, color: "var(--text)" }}>
                          {c.displayName ?? c.title}
                          {/* The live period/window suffix, shown muted so the title reads
                              exactly like the page (founder 2026-07-13: "Total units · {timeline}"). */}
                          {c.titleSuffix && (
                            <span style={{ color: "var(--body-faint)", fontWeight: 500, fontSize: 13 }}> · {c.titleSuffix}</span>
                          )}
                          {c.pagesLabel && c.pagesLabel !== c.page && (
                            <span style={{ color: "var(--body-faint)", fontWeight: 500, fontSize: 12 }}> · {c.pagesLabel}</span>
                          )}
                        </div>
                        <div style={{ color: "var(--body-faint)", fontSize: 12, marginTop: 2 }}>
                          {c.definition}
                        </div>
                      </div>
                      {noted && <span style={styles.notedChip}>Noted</span>}
                      {!noted && c.feedbackSubmittedAt && <span style={styles.submittedChip}>Feedback submitted</span>}
                      {c.noteChanged && <span style={styles.noteChangedChip}>Note wording changed</span>}
                      <span style={chip(c.status)}>{statusLabel(c.status)}</span>
                      <span style={{ color: "var(--body-faint)", fontSize: 12 }}>{isOpen ? "▲" : "▼"}</span>
                    </button>
                    {/* Opens the REAL card on the demo account and flashes it. Disabled
                        (with a reason) when the card has no demo surface. */}
                    {c.previewPath ? (
                      <a
                        href={c.previewPath}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={`Open ${c.previewPage ?? "the demo"} and highlight this card`}
                        style={styles.seeLive}
                      >
                        See it live →
                      </a>
                    ) : (
                      <span style={styles.seeLiveDisabled} title="No demo surface for this card yet">
                        See it live →
                      </span>
                    )}
                  </div>

                  {isOpen && (
                    <div style={{ padding: "0 16px 15px" }}>
                      {/* SEE THE CARD, in place (founder 2026-07-13: "how can I
                          approve it without being able to see the card?"). The real
                          card rendered on demo data, framed chrome-free via ?embed=1.
                          Only mounts while THIS row is open (one row expands at a
                          time), so 83 rows never load at once; it unmounts on
                          collapse. Cards with no demo surface show the reason. */}
                      {c.previewPath ? (
                        <CardLivePreview path={c.previewPath} page={c.previewPage} />
                      ) : (
                        <div style={styles.previewNone}>
                          No live preview yet — this card has no demo surface, so it can&rsquo;t be shown on the demo account. The math and worked example below are the way to review it.
                        </div>
                      )}
                      <Field label="What it is">{c.definition}</Field>
                      <Field label="How it's worked out">{c.arithmetic}</Field>
                      <Field label="Worked example">{c.example}</Field>
                      <Field label="With the date picker">{c.windowBehavior}</Field>
                      {c.note && <Field label="Note">{c.note}</Field>}
                      {/* Operator-only meta (consolidations, library-only, plumbing) — never a
                          client-facing box, never shown in the ⓘ (founder 2026-07-13). */}
                      {c.opsNote && (
                        <div style={{ marginTop: 9, fontSize: 11.5, color: "var(--body-faint)", lineHeight: 1.5, display: "flex", gap: 6, alignItems: "flex-start" }}>
                          <span style={{ fontWeight: 700, whiteSpace: "nowrap" }}>⚙ Operator note</span>
                          <span style={{ fontStyle: "italic" }}>{c.opsNote}</span>
                        </div>
                      )}
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginTop: 10, alignItems: "center" }}>
                        {c.metricIds.length > 0 && (
                          <span style={{ color: "var(--body-faint)", fontSize: 11.5 }}>
                            Metric: {c.metricIds.join(", ")}
                          </span>
                        )}
                        <span style={{ color: "var(--body-faint)", fontSize: 11.5 }}>
                          {c.grandfathered ? "Existing card (stays visible while pending)" : "New card (hidden until approved)"}
                        </span>
                        {c.approvedAt && (
                          <span style={{ color: "var(--body-faint)", fontSize: 11.5 }}>
                            {c.status === "rejected" ? "Rejected" : "Approved"} {new Date(c.approvedAt).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                      {c.status === "changed" && (
                        <div style={styles.changedNote}>
                          The wording or math changed since this was approved — re-approve to confirm clients see the new version.
                        </div>
                      )}
                      {c.notes && (
                        <div style={{ color: "var(--body-faint)", fontSize: 12, marginTop: 8, fontStyle: "italic" }}>
                          Note: {c.notes}
                        </div>
                      )}

                      {/* Customize how this card LOOKS to clients — title + the two
                          client-visible ⓘ lines. Saves by itself; applies to every
                          account (and the demo) on next load; never re-opens approval. */}
                      <div style={styles.customizeBox}>
                        <div style={{ fontSize: 12.5, fontWeight: 700, color: "var(--text)" }}>
                          How clients see this card
                        </div>
                        <div style={{ fontSize: 11.5, color: "var(--body-faint)", marginTop: 2 }}>
                          The recommended wording is already in each box — edit anything and it saves by itself.
                        </div>
                        {ovField(
                          c,
                          "name",
                          "Card title",
                          c.title,
                          c.titleSuffix
                            ? `Renders live as “${renderedTitle(c.title, c.titleSuffix)}” — you edit only the base “${c.title}”; the “${c.titleSuffix}” is the window label the page adds automatically. Clear to restore the default.`
                            : `Default: “${c.title}”. Clear to restore it.`,
                          false,
                        )}
                        {ovField(c, "what", "Info card — What it is", c.definition, "The first line clients read in the ⓘ popover. Clear to restore the default.", true)}
                        {ovField(c, "note", "Info card — Note", c.note ?? "No note by default.", "The “Note” line clients read in the ⓘ popover. Clear to restore the default.", true)}
                        {/* The old "Blended" toggle was removed (founder 2026-07-14):
                            it never touched the math — whether a card can split by
                            product is fixed by the data (spend has no per-product
                            grain, so CAC/MER are permanently blended). The blended
                            signal now lives in the card TITLE, which the operator
                            edits above. */}
                      </div>

                      {/* Card-review draft note — autosaves on blur, independent of
                          approve/reject. Submit it from the bar at the top of the page. */}
                      <div style={{ marginTop: 15 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <div style={styles.noteLabel}>What's wrong / what should change — or your question</div>
                          {saveState[c.id] === "saving" && <span style={saveChip("var(--body-faint)")}>Saving…</span>}
                          {saveState[c.id] === "saved" && <span style={saveChip("var(--good)")}>Saved ✓</span>}
                          {saveState[c.id] === "error" && (
                            <button type="button" style={{ ...saveChip("var(--bad)"), cursor: "pointer", border: "none" }}
                              onClick={() => saveNote.mutate({ id: c.id, note: noteValue(c) })}>
                              Couldn't save — Retry
                            </button>
                          )}
                        </div>
                        <textarea
                          value={noteValue(c)}
                          onChange={(e) => onNoteChange(c.id, e.target.value)}
                          onBlur={() => flushNote(c)}
                          placeholder="Type here — it saves by itself as you type. Nothing is sent until you hit Submit feedback."
                          style={styles.noteBox}
                        />
                        <div style={{ fontSize: 11.5, color: "var(--body-faint)", marginTop: 5 }}>
                          {noted
                            ? "Saved as a draft — select it up top and hit Submit feedback to send it to Feedback & Fixes."
                            : "Saves by itself while you type."}
                          {!noted && c.feedbackSubmittedAt
                            ? ` · Last submitted ${new Date(c.feedbackSubmittedAt).toLocaleDateString()}.`
                            : ""}
                        </div>
                      </div>

                      <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
                        <button
                          type="button"
                          disabled={decide.isPending}
                          onClick={() => {
                            flushNote(c); // never lose typed text on a decision click
                            decide.mutate({ id: c.id, action: "approve" });
                          }}
                          style={c.status === "approved" ? styles.ghostBtn : styles.approveBtn}
                        >
                          {c.status === "approved" ? "Approved ✓" : "Approve"}
                        </button>
                        <button
                          type="button"
                          disabled={ask.isPending}
                          onClick={() => {
                            const text = noteValue(c).trim();
                            if (!text) {
                              toast.error("Type your question in the box first, then hit Ask a question.");
                              return;
                            }
                            const timers = saveTimers.current;
                            if (timers[c.id]) {
                              clearTimeout(timers[c.id]);
                              delete timers[c.id];
                            }
                            ask.mutate({ id: c.id, question: text });
                          }}
                          style={c.status === "question" ? styles.ghostBtn : styles.askBtn}
                        >
                          {c.status === "question" ? "In TBD ?" : "Ask a question"}
                        </button>
                        <button
                          type="button"
                          disabled={decide.isPending}
                          onClick={() => {
                            flushNote(c);
                            if (!window.confirm(`Reject "${c.title}"? It will be hidden from real client accounts.`)) return;
                            const notes = window.prompt("Optional: why is this rejected? (leave blank to skip)") ?? undefined;
                            decide.mutate({ id: c.id, action: "reject", notes: notes || undefined });
                          }}
                          style={c.status === "rejected" ? styles.ghostBtn : styles.rejectBtn}
                        >
                          {c.status === "rejected" ? "Rejected" : "Reject"}
                        </button>
                      </div>
                    </div>
                  )}
                </Card>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }): React.ReactElement {
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ color: "var(--accent)", fontSize: 11, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase" }}>
        {label}
      </div>
      <div style={{ color: "var(--body)", fontSize: 13.5, lineHeight: 1.5, marginTop: 3 }}>{children}</div>
    </div>
  );
}

/**
 * The real card, rendered live on demo data, embedded in the review row. `path`
 * is the demo deep-link the server already built (e.g.
 * "/demo/financials?spotlight=fin-roic"); we add `&embed=1` so the demo strips
 * its sidebar/period-bar chrome and the frame is just the card, scrolled and
 * flashed by the existing ?spotlight= mechanism. Same-origin, so the frame just
 * works (the Pages _headers allow frame-ancestors 'self'). Sandboxed to
 * scripts + same-origin (a first-party SPA needs both to run and to reach its
 * own API/localStorage). A skeleton covers the load. It only mounts while the
 * row is open, so the 83-row list never loads 83 iframes.
 */
function CardLivePreview({ path, page }: { path: string; page: string | null }): React.ReactElement {
  const [loaded, setLoaded] = useState(false);
  const src = `${path}${path.includes("?") ? "&" : "?"}embed=1`;
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 6 }}>
        <div style={styles.noteLabel}>Live preview · demo data</div>
        <a href={path} target="_blank" rel="noopener noreferrer" style={styles.previewOpen}
          title={`Open ${page ?? "the demo"} full-page in a new tab`}>
          Open full page →
        </a>
      </div>
      <div style={styles.previewFrameWrap}>
        {!loaded && (
          <div style={styles.previewSkeleton}>
            <span style={{ fontSize: 12.5, color: "var(--body-faint)" }}>Loading the live card…</span>
          </div>
        )}
        <iframe
          src={src}
          title={`Live preview — ${page ?? "demo"}`}
          onLoad={() => setLoaded(true)}
          loading="lazy"
          sandbox="allow-scripts allow-same-origin"
          style={{ ...styles.previewIframe, opacity: loaded ? 1 : 0 }}
        />
      </div>
      <div style={{ fontSize: 11.5, color: "var(--body-faint)", marginTop: 5 }}>
        Sample data from the demo account — scroll inside the frame to explore around the highlighted card.
      </div>
    </div>
  );
}

/** Optimistically fold a note edit into the cached cards payload so the "Noted"
 *  chip and the Noted-tab count update the instant you click away (before the
 *  server round-trip settles). */
function applyNoteToCache(prev: CardsPayload, id: string, note: string): CardsPayload {
  const draftNote = note.trim().length > 0 ? note : null;
  let noted = 0;
  const cards = prev.cards.map((c) => {
    const next = c.id === id ? { ...c, draftNote } : c;
    if (next.draftNote && next.draftNote.trim()) noted += 1;
    return next;
  });
  return { ...prev, cards, counts: { ...prev.counts, noted } };
}

function statusLabel(s: DerivedStatus): string {
  return s === "approved" ? "Approved ✓" : s === "rejected" ? "Rejected" : s === "changed" ? "Changed" : s === "question" ? "TBD ?" : "Pending";
}

/** iOS-style toggle track. `on` = accent; `disabled` = muted + not-allowed. */
/** The Saving…/Saved ✓/Retry chip next to the note label. */
function saveChip(tone: string): React.CSSProperties {
  return {
    fontSize: 10.5,
    fontWeight: 700,
    padding: "2px 8px",
    borderRadius: 999,
    color: tone,
    background: `color-mix(in srgb, ${tone} 12%, transparent)`,
  };
}

function chip(s: DerivedStatus): React.CSSProperties {
  const tone =
    s === "approved" ? "var(--good)" : s === "rejected" ? "var(--bad)" : s === "changed" ? "var(--accent)" : s === "question" ? "var(--warn, #b5820a)" : "var(--body-faint)";
  return {
    fontSize: 11,
    fontWeight: 700,
    padding: "3px 9px",
    borderRadius: 999,
    whiteSpace: "nowrap",
    color: tone,
    background: `color-mix(in srgb, ${tone} 14%, transparent)`,
  };
}

const styles: Record<string, React.CSSProperties> = {
  kicker: { color: "var(--accent)", fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" },
  groupHead: { fontSize: 13, fontWeight: 700, color: "var(--text)", borderBottom: "1px solid var(--border)", paddingBottom: 7 },
  rowBtn: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "13px 16px",
    background: "transparent",
    border: "none",
    cursor: "pointer",
  },
  seeLive: {
    flexShrink: 0,
    alignSelf: "center",
    marginRight: 14,
    padding: "6px 11px",
    borderRadius: "var(--r-sm)",
    border: "1px solid color-mix(in srgb, var(--accent) 45%, var(--border))",
    background: "color-mix(in srgb, var(--accent) 12%, var(--surface))",
    color: "var(--accent)",
    fontSize: 12.5,
    fontWeight: 600,
    textDecoration: "none",
    whiteSpace: "nowrap",
  },
  seeLiveDisabled: {
    flexShrink: 0,
    alignSelf: "center",
    marginRight: 14,
    padding: "6px 11px",
    borderRadius: "var(--r-sm)",
    border: "1px solid var(--border)",
    background: "var(--surface-2)",
    color: "var(--body-faint)",
    fontSize: 12.5,
    fontWeight: 600,
    whiteSpace: "nowrap",
    cursor: "not-allowed",
  },
  previewOpen: {
    fontSize: 12,
    fontWeight: 600,
    color: "var(--accent)",
    textDecoration: "none",
    whiteSpace: "nowrap",
  },
  previewFrameWrap: {
    position: "relative",
    height: 480,
    borderRadius: "var(--r-sm)",
    border: "1px solid var(--border)",
    overflow: "hidden",
    background: "var(--surface-2)",
  },
  previewSkeleton: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "var(--surface-2)",
  },
  previewIframe: {
    width: "100%",
    height: "100%",
    border: "none",
    display: "block",
    background: "var(--bg)",
    transition: "opacity .25s ease",
  },
  previewNone: {
    marginTop: 12,
    padding: "11px 13px",
    borderRadius: "var(--r-sm)",
    border: "1px dashed var(--border)",
    background: "var(--surface-2)",
    color: "var(--body-faint)",
    fontSize: 12.5,
    lineHeight: 1.5,
  },
  search: {
    flex: 1,
    minWidth: 180,
    maxWidth: 320,
    padding: "7px 11px",
    borderRadius: "var(--r-sm)",
    border: "1px solid var(--border)",
    background: "var(--surface)",
    color: "var(--text)",
    fontSize: 13,
  },
  changedNote: {
    marginTop: 10,
    padding: "8px 11px",
    borderRadius: "var(--r-sm)",
    background: "color-mix(in srgb, var(--accent) 10%, var(--surface))",
    border: "1px solid color-mix(in srgb, var(--accent) 35%, var(--border))",
    color: "var(--body)",
    fontSize: 12.5,
  },
  approveBtn: {
    border: "1px solid color-mix(in srgb, var(--good) 45%, var(--border))",
    background: "color-mix(in srgb, var(--good) 14%, var(--surface))",
    color: "var(--good)",
    borderRadius: "var(--r-sm)",
    padding: "7px 15px",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
  askBtn: {
    border: "1px solid color-mix(in srgb, var(--warn, #b5820a) 45%, var(--border))",
    background: "color-mix(in srgb, var(--warn, #b5820a) 12%, var(--surface))",
    color: "var(--warn, #b5820a)",
    borderRadius: "var(--r-sm)",
    padding: "7px 15px",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
  rejectBtn: {
    border: "1px solid color-mix(in srgb, var(--bad) 45%, var(--border))",
    background: "color-mix(in srgb, var(--bad) 12%, var(--surface))",
    color: "var(--bad)",
    borderRadius: "var(--r-sm)",
    padding: "7px 15px",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
  ghostBtn: {
    border: "1px solid var(--border)",
    background: "var(--surface-2)",
    color: "var(--body)",
    borderRadius: "var(--r-sm)",
    padding: "7px 15px",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
  checkSlot: { display: "flex", alignItems: "center", justifyContent: "center", width: 34, flexShrink: 0 },
  notedChip: {
    fontSize: 11,
    fontWeight: 700,
    padding: "3px 9px",
    borderRadius: 999,
    whiteSpace: "nowrap",
    color: "var(--accent)",
    background: "color-mix(in srgb, var(--accent) 14%, transparent)",
  },
  submittedChip: {
    fontSize: 11,
    fontWeight: 700,
    padding: "3px 9px",
    borderRadius: 999,
    whiteSpace: "nowrap",
    color: "var(--good)",
    background: "color-mix(in srgb, var(--good) 14%, transparent)",
  },
  // Amber — this card is in review because WE reworded its note/definition (no math
  // change), so he knows what changed and can re-approve. Cleared on re-approve.
  noteChangedChip: {
    fontSize: 11,
    fontWeight: 700,
    padding: "3px 9px",
    borderRadius: 999,
    whiteSpace: "nowrap",
    color: "#b45309",
    background: "color-mix(in srgb, #d97706 15%, transparent)",
  },
  submitBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
    marginTop: 14,
    padding: "10px 14px",
    borderRadius: "var(--r-sm)",
    background: "color-mix(in srgb, var(--accent) 7%, var(--surface))",
    border: "1px solid color-mix(in srgb, var(--accent) 30%, var(--border))",
  },
  submitBtn: {
    border: "none",
    background: "var(--accent)",
    color: "var(--accent-contrast)",
    borderRadius: "var(--r-sm)",
    padding: "8px 16px",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
  submitBtnDisabled: {
    border: "none",
    background: "var(--surface-2)",
    color: "var(--body-faint)",
    borderRadius: "var(--r-sm)",
    padding: "8px 16px",
    fontSize: 13,
    fontWeight: 600,
    cursor: "not-allowed",
  },
  linkBtn: {
    border: "none",
    background: "transparent",
    color: "var(--accent)",
    fontSize: 12.5,
    fontWeight: 600,
    cursor: "pointer",
    padding: "4px 6px",
  },
  noteLabel: {
    color: "var(--accent)",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    marginBottom: 5,
  },
  noteBox: {
    width: "100%",
    minHeight: 68,
    resize: "vertical",
    padding: "9px 11px",
    borderRadius: "var(--r-sm)",
    border: "1px solid var(--border)",
    background: "var(--surface)",
    color: "var(--text)",
    fontSize: 13,
    fontFamily: "inherit",
    lineHeight: 1.5,
    boxSizing: "border-box",
  },
  ovInput: {
    width: "100%",
    padding: "8px 11px",
    borderRadius: "var(--r-sm)",
    border: "1px solid var(--border)",
    background: "var(--surface)",
    color: "var(--text)",
    fontSize: 13,
    fontFamily: "inherit",
    boxSizing: "border-box",
  },
  customizeBox: {
    marginTop: 15,
    padding: "12px 13px",
    borderRadius: "var(--r-sm)",
    background: "color-mix(in srgb, var(--accent) 5%, var(--surface))",
    border: "1px solid color-mix(in srgb, var(--accent) 22%, var(--border))",
  },
};
